// @ts-nocheck

import axiosLib from "axios";

/** instance for protected/authenticated endpoints */
export const axios = axiosLib.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
});
console.log("creating axios with base url", process.env.REACT_APP_SERVER_URL);

// Callback for handling 401 errors - set from App.js
let onUnauthorized = null;
let onAccountantContextInvalid = null;

/** Set callback to handle 401 unauthorized responses (triggers login redirect) */
export const setOnUnauthorized = (callback) => {
  onUnauthorized = callback;
};

/** Set callback for invalid/expired accountant context token */
export const setOnAccountantContextInvalid = (callback) => {
  onAccountantContextInvalid = callback;
};

const isAccountantControlRoute = (url = "") => {
  return /^\/api\/accountant(?:-invites)?(?:\/|$)/.test(url)
    || /^\/accountant(?:-invites)?(?:\/|$)/.test(url);
};

const isAccountantContextAuthError = (error) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.error || "").toLowerCase();
  const hadContextHeader = !!error?.config?.headers?.["X-Accountant-Context"];

  if (!hadContextHeader || ![401, 403].includes(status)) {
    return false;
  }

  return (
    message.includes("context token")
    || message.includes("invalid context")
    || message.includes("client user not found")
    || message.includes("no access to this client")
  );
};

/** Add error interceptor for analytics tracking and 401 handling */
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAccountantContextAuthError(error) && onAccountantContextInvalid) {
      onAccountantContextInvalid();
    }

    // Handle 401 unauthorized - trigger login redirect
    if (error.response?.status === 401 && onUnauthorized) {
      console.log("[axios] 401 Unauthorized - triggering login redirect");
      onUnauthorized();
      return Promise.reject(error);
    }

    // Track API errors
    try {
      const { trackEvent } = require("@/analytics");
      const { ERROR_RESILIENCE_EVENTS } = require("@/analytics/events");

      if (error.response) {
        // Server responded with error status
        trackEvent(ERROR_RESILIENCE_EVENTS.API_REQUEST_FAILED, {
          endpoint: error.config?.url || "unknown",
          method: error.config?.method || "unknown",
          status_code: error.response.status,
          error_type: "http_error",
          source: "web",
        });
      } else if (error.request) {
        // Request made but no response
        trackEvent(ERROR_RESILIENCE_EVENTS.NETWORK_ERROR, {
          endpoint: error.config?.url || "unknown",
          method: error.config?.method || "unknown",
          error_type: "network_error",
          source: "web",
        });
      } else {
        // Error in request setup
        trackEvent(ERROR_RESILIENCE_EVENTS.API_REQUEST_FAILED, {
          error_type: "request_setup_error",
          error_message: error.message,
          source: "web",
        });
      }
    } catch (trackingError) {
      console.error("[axios] Error tracking API failure:", trackingError);
    }

    return Promise.reject(error);
  }
);

// Accountant context token — set when CPA is viewing a client
let accountantContextToken = null;

/** Set or clear the accountant context token for API calls */
export const setAccountantContextToken = (token) => {
  accountantContextToken = token;
};

// Request interceptor: attach accountant context header when viewing a client
axios.interceptors.request.use((config) => {
  if (accountantContextToken && !isAccountantControlRoute(config.url || "")) {
    config.headers["X-Accountant-Context"] = accountantContextToken;
  }
  return config;
});

/** function to set the instances headers when we get the access token */
export const setAuthToken = (token) => {
  console.log("[axios] set access token");
  if (token) {
    //applying token
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    //deleting the token from header
    delete axios.defaults.headers.common["Authorization"];
  }
};

/** sets the header in the components after calling getAccessTokenSilently() */
export const authorizedAxios = (token) => {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  return axios;
};

export default axios;
